import { useEffect, useRef } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useJoinMutation } from '@proaxiv/server-api'
import { keycloak } from '../auth/keycloak'
import { useActiveOrg } from '../contexts/ActiveOrgContext'
import { useAuth } from '../auth/AuthProvider'

const CheckIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9 12l2 2 4-4" />
  </svg>
)

const AlertIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4M12 16h.01" />
  </svg>
)

const SpinnerIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="animate-spin"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
)

/**
 * Handles the `/join?token=...` route generated by invitation emails.
 *
 * Reads the token from the query string, calls `POST /api/v1/organizations/join`,
 * and shows loading / success / error feedback. On success the user is redirected
 * to `/home` after a short delay.
 */
export function JoinOrgPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const { setActiveOrgId, refetchOrgs } = useActiveOrg()
  const { email: currentEmail, logout } = useAuth()

  const [join, { isSuccess, isError, error, data }] = useJoinMutation()
  const calledRef = useRef(false)

  useEffect(() => {
    if (!token || calledRef.current) return
    calledRef.current = true

    // The user may have arrived here with a stale JWT still cached from a
    // previous session (e.g. the tab was left open, or Keycloak's in-memory
    // token wasn't refreshed since before the invitation was created). Force
    // a refresh first so the join call is authenticated with an up-to-date
    // token/email claim — otherwise the backend can reject a perfectly valid
    // invitation with a confusing "Invitation not found" error.
    keycloak
      .updateToken(-1)
      .catch(() => {
        // Refresh failed — proceed anyway with whatever token we have; the
        // join call itself will surface a clear error if auth is the issue.
      })
      .finally(() => {
        join({ joinBody: { token } })
      })
  }, [token, join])

  useEffect(() => {
    if (!isSuccess) return

    // Set the joined org as active so /home opens it directly.
    const joinedOrgId = (data as { orgId?: string } | undefined)?.orgId
    if (joinedOrgId) setActiveOrgId(joinedOrgId)

    // Force a fresh `listOrgs` fetch rather than relying solely on the `join`
    // mutation's `invalidatesTags`. On a first-ever visit (fresh login via the
    // invitation email), the org list's very first request can still be in
    // flight when `join` resolves — RTK Query only records a query's tags once
    // it fulfills, so an invalidation that lands mid-flight has nothing to
    // attach to and is dropped, leaving /home stuck showing the "create an
    // organization" empty state forever. An explicit forced refetch here always
    // starts a brand-new request that reflects the post-join membership.
    refetchOrgs?.()

    // Force a token refresh so the new org_ids claim is in the JWT before
    // GET /organizations/{orgId} is called on /home (ACCESS_ORG checks it).
    // The 2-second success screen gives plenty of time for the refresh to complete.
    keycloak.updateToken(-1).catch(() => {
      // Refresh failed — navigate anyway; the next periodic refresh will fix it.
    })

    const timer = setTimeout(() => navigate('/home', { replace: true }), 2000)
    return () => clearTimeout(timer)
  }, [isSuccess, navigate, data, setActiveOrgId, refetchOrgs])

  const errorData = (
    error as { data?: { errorCode?: string; message?: string; invitedEmail?: string } } | undefined
  )?.data
  const errorCode = errorData?.errorCode
  // Only the FORBIDDEN (wrong-account) case swaps "Go to home" for a log-out button — every
  // other error is unrelated to which account the user is logged in as.
  const isEmailMismatch = errorCode === 'FORBIDDEN'

  const resolvedErrorMessage = (() => {
    switch (errorCode) {
      case 'NOT_FOUND':
        return t('join_err_not_found')
      case 'INVALID_STATUS':
        return t('join_err_invalid_status')
      case 'FORBIDDEN':
        return t('join_err_forbidden', {
          invitedEmail: errorData?.invitedEmail || t('join_err_forbidden_unknown_email'),
          currentEmail: currentEmail || t('join_err_forbidden_unknown_email'),
        })
      case 'ALREADY_MEMBER':
        return t('join_err_already_member')
      default:
        return errorData?.message ?? t('join_err_generic')
    }
  })()

  // Derive the display state. While we have a token and haven't received a
  // terminal response yet we are in the "loading" phase (covers the tiny gap
  // between mount and the effect firing as well as the in-flight request).
  const showLoading = Boolean(token) && !isSuccess && !isError
  const showNoToken = !token

  return (
    <div className="mx-auto flex max-w-5xl flex-col items-center justify-center px-4 py-20 md:px-6">
      <div className="border-pax-purple/20 flex w-full max-w-md flex-col items-center gap-6 rounded-2xl border bg-white p-10 text-center shadow-sm">
        {showNoToken && (
          <>
            <div className="text-red-500">
              <AlertIcon />
            </div>
            <div className="flex flex-col gap-2">
              <h1 className="font-display text-pax-black text-xl font-bold">
                {t('join_invalid_title')}
              </h1>
              <p className="text-pax-gray text-sm leading-relaxed">{t('join_invalid_body')}</p>
            </div>
            <Link
              to="/home"
              replace
              className="bg-pax-blue hover:bg-pax-blue/90 inline-flex w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold text-white transition-colors focus-visible:outline-none"
            >
              {t('join_go_home')}
            </Link>
          </>
        )}

        {showLoading && (
          <>
            <div className="text-pax-blue">
              <SpinnerIcon />
            </div>
            <div className="flex flex-col gap-1">
              <h1 className="font-display text-pax-black text-xl font-bold">
                {t('join_loading_title')}
              </h1>
              <p className="text-pax-gray text-sm">{t('join_loading_body')}</p>
            </div>
          </>
        )}

        {isSuccess && (
          <>
            <div className="text-green-500">
              <CheckIcon />
            </div>
            <div className="flex flex-col gap-2">
              <h1 className="font-display text-pax-black text-xl font-bold">
                {t('join_success_title')}
              </h1>
              <p className="text-pax-gray text-sm leading-relaxed">{t('join_success_body')}</p>
            </div>
          </>
        )}

        {isError && (
          <>
            <div className="text-red-500">
              <AlertIcon />
            </div>
            <div className="flex flex-col gap-2">
              <h1 className="font-display text-pax-black text-xl font-bold">
                {t('join_error_title')}
              </h1>
              <p className="text-pax-gray text-sm leading-relaxed">{resolvedErrorMessage}</p>
            </div>
            {isEmailMismatch ? (
              <button
                type="button"
                onClick={logout}
                className="bg-pax-blue hover:bg-pax-blue/90 inline-flex w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold text-white transition-colors focus-visible:outline-none"
              >
                {t('header_signout')}
              </button>
            ) : (
              <Link
                to="/home"
                replace
                className="bg-pax-blue hover:bg-pax-blue/90 inline-flex w-full items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold text-white transition-colors focus-visible:outline-none"
              >
                {t('join_go_home')}
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  )
}
